import React, { useState, useEffect } from 'react';
import { db, auth } from './firebase';
import { doc, updateDoc, serverTimestamp, collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import IncidentModal from './IncidentModal';
import { can } from './roles';
import { downloadEvidence } from './evidenceExport';

const S = {
  page: { padding: '0 24px 24px' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 0 16px' },
  title: { fontSize: 20, fontWeight: 900, color: '#1A1A2E' },
  sub: { fontSize: 12, color: '#9B8F7A', marginTop: 2 },
  filters: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 },
  filterSelect: { background: '#FFFFFF', border: '1px solid #E2DDD6', color: '#1A1A2E', padding: '7px 12px', borderRadius: 8, fontSize: 12, cursor: 'pointer' },
  filterInput: { background: '#FFFFFF', border: '1px solid #E2DDD6', color: '#1A1A2E', padding: '7px 12px', borderRadius: 8, fontSize: 12, width: 200 },
  btn: (color) => ({ background: color || '#1E3A5F', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700 }),
  card: { background: '#FFFFFF', border: '1px solid #E2DDD6', borderRadius: 12, overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '10px 14px', fontSize: 11, color: '#9B8F7A', borderBottom: '1px solid #EAE6DE', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, whiteSpace: 'nowrap' },
  td: { padding: '12px 14px', fontSize: 13, borderBottom: '1px solid #1E3A5F11', verticalAlign: 'middle' },
  badge: (color) => ({ background: color + '22', color, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, display: 'inline-block', whiteSpace: 'nowrap' }),
  detail: { background: '#FFFFFF', border: '1px solid #E2DDD6', borderRadius: 12, padding: 24, width: 380, flexShrink: 0 },
  detailLabel: { fontSize: 11, color: '#9B8F7A', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 6, marginTop: 16 },
  detailValue: { fontSize: 13, color: '#1A1A2E', lineHeight: 1.6 },
  stats: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 16 },
  statCard: (color) => ({ background: '#FFFFFF', border: `1px solid ${color}44`, borderRadius: 10, padding: '14px 16px' }),
};

const escalationColor = (level) => {
  if (!level) return '#2E7D32';
  if (level.includes('3') || level.includes('Emergency')) return '#D32F2F';
  if (level.includes('2') || level.includes('Serious')) return '#F57F17';
  return '#2E7D32';
};

const statusColor = (status) => {
  if (status === 'open') return '#D32F2F';
  if (status === 'under_review') return '#F57F17';
  return '#2E7D32';
};

const formatTime = (ts) => {
  if (!ts) return '';
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
};

// Only allow http(s) URLs — blocks javascript:/data: schemes that would execute
// as stored XSS when an operator clicks the evidence link (value comes from Firestore).
const safeHttpUrl = (u) => {
  if (!u || typeof u !== 'string') return null;
  try {
    const p = new URL(u, window.location.origin);
    return (p.protocol === 'http:' || p.protocol === 'https:') ? p.href : null;
  } catch { return null; }
};

// One evidence item — image or video — with loading/error states and an
// optional gated Download button. NOTE: no loading="lazy" on the media (a
// lazy + display:none element never starts loading — the bug we fixed before).
function EvidenceItem({ url, kind, onDownload }) {
  const [status, setStatus] = useState('loading'); // loading | loaded | error
  const [downloading, setDownloading] = useState(false);
  const [dlErr, setDlErr] = useState('');
  const safe = safeHttpUrl(url);
  if (!safe) return null;

  const doDownload = async () => {
    setDownloading(true); setDlErr('');
    try { await onDownload(url); }
    catch (e) { setDlErr(e.message || 'Download failed'); }
    finally { setDownloading(false); }
  };

  return (
    <div style={{ marginTop: 8 }}>
      {kind === 'video' ? (
        <>
          {status !== 'error' && (
            <video src={safe} controls preload="metadata" referrerPolicy="no-referrer"
              onLoadedMetadata={() => setStatus('loaded')} onError={() => setStatus('error')}
              style={{ width: '100%', borderRadius: 8, border: '1px solid #E2DDD6', background: '#000', display: status === 'loaded' ? 'block' : 'none' }} />
          )}
          {status === 'loading' && <div style={{ ...S.detailValue, color: '#9B8F7A', fontSize: 12 }}>Loading video…</div>}
          {status === 'error' && <div style={{ ...S.detailValue, color: '#9B8F7A', fontSize: 12 }}>Video unavailable.</div>}
        </>
      ) : (
        <>
          {status !== 'error' && (
            <a href={safe} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer" style={{ display: 'block' }}>
              <img src={safe} alt="Incident evidence" referrerPolicy="no-referrer"
                onLoad={() => setStatus('loaded')} onError={() => setStatus('error')}
                style={{ width: '100%', borderRadius: 8, border: '1px solid #E2DDD6', display: status === 'loaded' ? 'block' : 'none' }} />
            </a>
          )}
          {status === 'loading' && <div style={{ ...S.detailValue, color: '#9B8F7A', fontSize: 12 }}>Loading photo…</div>}
          {status === 'error' && <div style={{ ...S.detailValue, color: '#9B8F7A', fontSize: 12 }}>Photo unavailable.</div>}
        </>
      )}
      {onDownload && status !== 'error' && (
        <button onClick={doDownload} disabled={downloading}
          style={{ ...S.btn('#1E3A5F'), marginTop: 6, padding: '5px 12px', fontSize: 11, opacity: downloading ? 0.6 : 1 }}>
          {downloading ? 'Preparing…' : '⬇ Download'}
        </button>
      )}
      {dlErr && <div style={{ color: '#D32F2F', fontSize: 11, marginTop: 4 }}>{dlErr}</div>}
    </div>
  );
}

// Evidence gallery: every image + video on the incident. Download per item is
// shown only when canExport (supervisor+); the button is also enforced by the
// exportEvidence Cloud Function, so hiding it is convenience, not the gate.
function EvidenceMedia({ incident, canExport }) {
  const images = [
    ...(Array.isArray(incident.imageUrls) ? incident.imageUrls : []),
    ...(incident.photo_url ? [incident.photo_url] : []),
  ].filter(Boolean);
  const videos = [
    ...(Array.isArray(incident.videoUrls) ? incident.videoUrls : []),
    ...(incident.video_url ? [incident.video_url] : []),
  ].filter(Boolean);
  if (images.length === 0 && videos.length === 0) return null;

  const recId = incident.id || incident._id;
  const onDownload = canExport ? (url) => downloadEvidence({ type: 'incident', id: recId, url }) : null;

  return (
    <>
      <div style={S.detailLabel}>
        Evidence ({images.length} photo{images.length === 1 ? '' : 's'}, {videos.length} video{videos.length === 1 ? '' : 's'})
      </div>
      {videos.map((u, i) => <EvidenceItem key={'v' + i} url={u} kind="video" onDownload={onDownload} />)}
      {images.map((u, i) => <EvidenceItem key={'i' + i} url={u} kind="image" onDownload={onDownload} />)}
    </>
  );
}

// Chain-of-custody export log — a simple tenant-scoped list of who exported
// which evidence file, when. Read is gated by the evidence_exports rule
// (supervisor+); this modal is only opened for those roles.
function ExportLogModal({ tenantId, onClose }) {
  const [rows, setRows] = useState(null); // null = loading
  const [err, setErr] = useState('');
  useEffect(() => {
    const q = query(collection(db, 'evidence_exports'), where('tenant_id', '==', tenantId), orderBy('exported_at', 'desc'));
    return onSnapshot(q,
      s => setRows(s.docs.map(d => ({ id: d.id, ...d.data() }))),
      () => { setErr('Could not load export log'); setRows([]); });
  }, [tenantId]);
  return (
    <div style={{ position: 'fixed', inset: 0, background: '#0A0E1Aaa', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 660, maxWidth: '95vw', maxHeight: '85vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#1A1A2E' }}>Evidence export log</div>
            <div style={{ fontSize: 12, color: '#9B8F7A' }}>Chain of custody — who exported which evidence file, when</div>
          </div>
          <button onClick={onClose} style={{ ...S.btn('#1E3A5F44'), color: '#1A1A2E' }}>Close</button>
        </div>
        {rows === null ? <div style={{ color: '#9B8F7A', padding: 20 }}>Loading…</div>
          : err ? <div style={{ color: '#D32F2F', padding: 20 }}>{err}</div>
          : rows.length === 0 ? <div style={{ color: '#9B8F7A', padding: 20 }}>No evidence has been exported yet.</div>
          : (
            <table style={S.table}>
              <thead><tr>{['When', 'Type', 'Record', 'Exported by', 'File'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id}>
                    <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{formatTime(r.exported_at)}</td>
                    <td style={S.td}><span style={S.badge(r.file_type === 'video' ? '#7C3AED' : '#1E3A5F')}>{r.file_type || 'file'}</span></td>
                    <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 11 }}>{r.incident_id || r.patrol_issue_id || '—'}</td>
                    <td style={{ ...S.td, fontSize: 12 }}>{r.exported_by_email || r.exported_by_uid}</td>
                    <td style={{ ...S.td, fontSize: 11, color: '#9B8F7A', wordBreak: 'break-all' }}>{(r.file_path || '').split('/').pop()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>
    </div>
  );
}

export default function IncidentsPage({ incidents, tenantId, userRole }) {
  const [search, setSearch] = useState('');
  const [filterLevel, setFilterLevel] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [selected, setSelected] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingIncident, setEditingIncident] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showExportLog, setShowExportLog] = useState(false);
  const canExport = can(userRole, 'exportEvidence');

  const categories = [...new Set(incidents.map(i => i.category).filter(Boolean))];

  const filtered = incidents.filter(inc => {
    if (filterLevel !== 'all' && !(inc.escalationLevel || '').includes(filterLevel)) return false;
    if (filterStatus !== 'all' && inc.status !== filterStatus) return false;
    if (filterCategory !== 'all' && inc.category !== filterCategory) return false;
    if (search && !(inc.siteName || '').toLowerCase().includes(search.toLowerCase()) &&
        !(inc.category || '').toLowerCase().includes(search.toLowerCase()) &&
        !(inc.guardName || '').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const stats = {
    total: incidents.length,
    open: incidents.filter(i => i.status === 'open').length,
    critical: incidents.filter(i => (i.escalationLevel || '').includes('3')).length,
    pending: incidents.filter(i => i.report_status === 'submitted' || i.report_status === 'pending_review').length,
  };

  const approveIncident = async (inc) => {
    if (!inc._id) return;
    await updateDoc(doc(db, 'incidents', inc._id), {
      report_status: 'approved',
      status: 'closed',
      approved_by: auth.currentUser?.email,
      approved_at: serverTimestamp(),
    });
    setSelected(prev => prev?._id === inc._id ? { ...prev, report_status: 'approved', status: 'closed' } : prev);
  };

  const closeIncident = async (inc) => {
    if (!inc._id) return;
    await updateDoc(doc(db, 'incidents', inc._id), {
      status: 'closed',
      closed_by: auth.currentUser?.email,
      closed_at: serverTimestamp(),
    });
  };

  const bulkApprove = async () => {
    for (const id of selectedIds) {
      const inc = incidents.find(i => i._id === id);
      if (inc) await approveIncident(inc);
    }
    setSelectedIds([]);
  };

  const exportCSV = () => {
    const headers = ['ID', 'Site', 'Category', 'Level', 'Status', 'Officer', 'Time'];
    const rows = filtered.map(i => [
      i.id || '', i.siteName || '', i.category || '',
      i.escalationLevel || '', i.status || '',
      i.guardName || '', formatTime(i.createdAt)
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'incidents.csv'; a.click();
  };

  return (
    <div style={S.page}>
      {showModal && (
        <IncidentModal
          incident={editingIncident}
          tenantId={tenantId}
          onClose={() => { setShowModal(false); setEditingIncident(null); }}
          onSaved={() => {}}
        />
      )}

      {showExportLog && <ExportLogModal tenantId={tenantId} onClose={() => setShowExportLog(false)} />}

      <div style={S.header}>
        <div>
          <div style={S.title}>Incident Management</div>
          <div style={S.sub}>Full incident log — create, review, approve and close</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {selectedIds.length > 0 && (
            <button style={S.btn('#2E7D32')} onClick={bulkApprove}>
              ✓ Approve {selectedIds.length} selected
            </button>
          )}
          {canExport && (
            <button style={{ ...S.btn('#1E3A5F44'), color: '#1A1A2E' }} onClick={() => setShowExportLog(true)}>
              Export log
            </button>
          )}
          <button style={S.btn()} onClick={exportCSV}>Export CSV</button>
          <button style={S.btn('#D32F2F')} onClick={() => { setEditingIncident(null); setShowModal(true); }}>
            + New Incident
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={S.stats}>
        <div style={S.statCard('#F8FAFC')}>
          <div style={{ fontSize: 11, color: '#9B8F7A', marginBottom: 4 }}>TOTAL</div>
          <div style={{ fontSize: 28, fontWeight: 900, color: '#1A1A2E' }}>{stats.total}</div>
        </div>
        <div style={S.statCard('#D32F2F')}>
          <div style={{ fontSize: 11, color: '#9B8F7A', marginBottom: 4 }}>OPEN</div>
          <div style={{ fontSize: 28, fontWeight: 900, color: '#D32F2F' }}>{stats.open}</div>
        </div>
        <div style={S.statCard('#F57F17')}>
          <div style={{ fontSize: 11, color: '#9B8F7A', marginBottom: 4 }}>CRITICAL</div>
          <div style={{ fontSize: 28, fontWeight: 900, color: '#F57F17' }}>{stats.critical}</div>
        </div>
        <div style={S.statCard('#C9A84C')}>
          <div style={{ fontSize: 11, color: '#9B8F7A', marginBottom: 4 }}>PENDING APPROVAL</div>
          <div style={{ fontSize: 28, fontWeight: 900, color: '#C9A84C' }}>{stats.pending}</div>
        </div>
      </div>

      {/* Filters */}
      <div style={S.filters}>
        <input style={S.filterInput} placeholder="Search site, category, officer…"
          value={search} onChange={e => setSearch(e.target.value)} />
        <select style={S.filterSelect} value={filterLevel} onChange={e => setFilterLevel(e.target.value)}>
          <option value="all">All levels</option>
          <option value="1">L1 Minor</option>
          <option value="2">L2 Serious</option>
          <option value="3">L3 Emergency</option>
        </select>
        <select style={S.filterSelect} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="all">All statuses</option>
          <option value="open">Open</option>
          <option value="under_review">Under review</option>
          <option value="closed">Closed</option>
        </select>
        <select style={S.filterSelect} value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
          <option value="all">All categories</option>
          {categories.map(c => <option key={c}>{c}</option>)}
        </select>
        {(search || filterLevel !== 'all' || filterStatus !== 'all' || filterCategory !== 'all') && (
          <button style={{ ...S.btn('#1E3A5F44'), color: '#9B8F7A' }}
            onClick={() => { setSearch(''); setFilterLevel('all'); setFilterStatus('all'); setFilterCategory('all'); }}>
            Clear filters
          </button>
        )}
      </div>

      {/* Table + Detail panel */}
      <div style={{ display: 'flex', gap: 16 }}>
        <div style={{ ...S.card, flex: 1, overflowX: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}><input type="checkbox" onChange={e => setSelectedIds(e.target.checked ? filtered.map(i => i._id).filter(Boolean) : [])} /></th>
                <th style={S.th}>ID</th>
                <th style={S.th}>Site</th>
                <th style={S.th}>Category</th>
                <th style={S.th}>Level</th>
                <th style={S.th}>Status</th>
                <th style={S.th}>Officer</th>
                <th style={S.th}>Time</th>
                <th style={S.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={9} style={{ ...S.td, textAlign: 'center', color: '#9B8F7A', padding: 32 }}>No incidents match filters.</td></tr>
              ) : filtered.map((inc, i) => (
                <tr key={i} style={{ cursor: 'pointer', background: selected?._id === inc._id ? '#1E3A5F22' : 'transparent' }}
                  onClick={() => setSelected(inc)}>
                  <td style={S.td} onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={selectedIds.includes(inc._id)}
                      onChange={e => setSelectedIds(prev => e.target.checked ? [...prev, inc._id] : prev.filter(id => id !== inc._id))} />
                  </td>
                  <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 11, color: '#9B8F7A' }}>{inc.id || inc._id?.substring(0, 8)}</td>
                  <td style={{ ...S.td, fontWeight: 600 }}>{inc.siteName || 'Unknown'}</td>
                  <td style={S.td}>{inc.category || 'N/A'}</td>
                  <td style={S.td}><span style={S.badge(escalationColor(inc.escalationLevel))}>{(inc.escalationLevel || 'L1').replace('Level ', 'L')}</span></td>
                  <td style={S.td}><span style={S.badge(statusColor(inc.status))}>{inc.status || 'open'}</span></td>
                  <td style={{ ...S.td, fontSize: 11, color: '#9B8F7A' }}>{inc.guardName || 'Unknown'}</td>
                  <td style={{ ...S.td, fontSize: 11, color: '#9B8F7A', whiteSpace: 'nowrap' }}>{formatTime(inc.createdAt)}</td>
                  <td style={S.td} onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button style={{ ...S.btn('#1E3A5F'), padding: '4px 10px', fontSize: 11 }}
                        onClick={() => { setEditingIncident(inc); setShowModal(true); }}>Edit</button>
                      {inc.status === 'open' && (
                        <button style={{ ...S.btn('#2E7D32'), padding: '4px 10px', fontSize: 11 }}
                          onClick={() => approveIncident(inc)}>Approve</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Detail panel */}
        {selected && (
          <div style={S.detail}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: '#1A1A2E' }}>Incident detail</div>
              <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', color: '#9B8F7A', cursor: 'pointer', fontSize: 18 }}>✕</button>
            </div>

            <span style={S.badge(escalationColor(selected.escalationLevel))}>{selected.escalationLevel || 'Level 1 Minor'}</span>
            <span style={{ ...S.badge(statusColor(selected.status)), marginLeft: 6 }}>{selected.status || 'open'}</span>

            <div style={S.detailLabel}>Incident ID</div>
            <div style={{ ...S.detailValue, fontFamily: 'monospace', fontSize: 11 }}>{selected.id || selected._id}</div>

            <div style={S.detailLabel}>Site</div>
            <div style={S.detailValue}>{selected.siteName || 'Unknown'}</div>

            <div style={S.detailLabel}>Category</div>
            <div style={S.detailValue}>{selected.category || 'N/A'}</div>

            <div style={S.detailLabel}>Officer</div>
            <div style={S.detailValue}>{selected.guardName || 'Unknown'}</div>

            <div style={S.detailLabel}>Time reported</div>
            <div style={S.detailValue}>{formatTime(selected.createdAt)}</div>

            <div style={S.detailLabel}>Description</div>
            <div style={{ ...S.detailValue, color: '#9B8F7A', fontSize: 12 }}>{selected.description || selected.full_description || 'No description.'}</div>

            <div style={S.detailLabel}>Action taken</div>
            <div style={{ ...S.detailValue, color: '#9B8F7A', fontSize: 12 }}>{selected.action_taken || 'None recorded.'}</div>

            <EvidenceMedia incident={selected} canExport={canExport} />

            {selected.police_reference_number && (
              <>
                <div style={S.detailLabel}>Police reference</div>
                <div style={S.detailValue}>{selected.police_reference_number}</div>
              </>
            )}

            <div style={S.detailLabel}>Emergency services</div>
            <div style={S.detailValue}>{selected.emergency_services_called ? '✓ Yes' : '✗ No'}</div>

            <div style={S.detailLabel}>Version</div>
            <div style={S.detailValue}>v{selected.version_number || 1}</div>

            <div style={{ display: 'flex', gap: 8, marginTop: 20, flexWrap: 'wrap' }}>
              <button style={{ ...S.btn('#1E3A5F'), flex: 1 }}
                onClick={() => { setEditingIncident(selected); setShowModal(true); }}>Edit</button>
              {selected.status === 'open' && (
                <button style={{ ...S.btn('#2E7D32'), flex: 1 }} onClick={() => approveIncident(selected)}>Approve & close</button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}